import { useState, useEffect, useCallback } from 'react'
import { appQueryClient } from '@/lib/queryClient'
import { api } from '@/lib/api'
import type { GenderCanonical } from '@/features/users/api/gender'
import { parseUserIdentity } from '@/features/auth/utils/parseUserIdentity'
import { authenticateBiometric, requestBiometricChallenge } from '@/features/auth/api/biometric'
import { loadDevice, signChallenge } from '@/lib/biometricDevice'
import { verifyUser } from '@/lib/biometrics'

export interface User {
  id: string
  username: string
  email: string
  displayName?: string
  avatarUrl?: string
  bio?: string
  /** Backend `UserDto` names (not `postsCount`/`followersCount`). */
  postCount: number
  followerCount: number
  followingCount: number
  bannerUrl?: string
  bannerType?: string
  posterUrl?: string
  country?: string
  gender?: string
  isVerified?: boolean
  /** Backend serialises the role enum as int or name; coerced to string. */
  role: string
  /** Account type chosen at registration: `client` | `creator` | `agency`. */
  accountType: string
  /** Voice-call reception: per-minute diamond rate (0 = free). Absent on legacy payloads. */
  callRatePerMinute?: number
  /** Voice-call reception master switch. Defaults to true. */
  callsEnabled?: boolean
  /** Backend serialises the KYC enum as int or name; coerced to string. */
  kycStatus: string
  /** Backend `UserDto.Phone`; absent for legacy payloads. */
  phone?: string
  /** Backend `UserDto.PhoneVerified`; R7 gates clients on it. Defaults to false. */
  phoneVerified: boolean
}

function toUser(raw: Record<string, unknown>): User {
  const identity = parseUserIdentity(raw)
  const count = (...values: unknown[]): number => {
    const found = values.find((v) => typeof v === 'number' && Number.isFinite(v))
    return (found as number | undefined) ?? 0
  }
  return {
    ...(raw as unknown as User),
    ...identity,
    // Backend `UserDto.Phone` / `PhoneVerified` (mirrors parseUserProfile).
    phone: typeof raw.phone === 'string' ? raw.phone : undefined,
    phoneVerified: raw.phoneVerified === true,
    // Legacy `postsCount`/`followersCount` are accepted defensively.
    postCount: count(raw.postCount, raw.postsCount),
    followerCount: count(raw.followerCount, raw.followersCount),
    followingCount: count(raw.followingCount),
    callRatePerMinute:
      typeof raw.callRatePerMinute === 'number' && Number.isInteger(raw.callRatePerMinute)
        ? raw.callRatePerMinute
        : 0,
    callsEnabled: raw.callsEnabled !== false,
  }
}

/** No biometric device is enrolled on this browser (mobile: `getDeviceId() == null`). */
export class BiometricNotConfiguredError extends Error {
  constructor() {
    super('Biometric login is not configured on this device')
    this.name = 'BiometricNotConfiguredError'
  }
}

/** The user dismissed the local verification prompt; callers return silently, like mobile. */
export class BiometricCancelledError extends Error {
  constructor() {
    super('Biometric verification was cancelled')
    this.name = 'BiometricCancelledError'
  }
}

/** The `{ user, tokens }` payload shared by login, register and the biometric endpoints. */
interface SessionPayload {
  user: Record<string, unknown>
  tokens: { accessToken: string; refreshToken: string }
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true
  })
  // Module singleton (see lib/queryClient): safe to use outside a
  // QueryClientProvider, unlike the useQueryClient() hook.
  const queryClient = appQueryClient

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      setState({ user: null, isAuthenticated: false, isLoading: false })
      return
    }

    try {
      const res = await api.get('/users/me')
      setState({ user: toUser(res.data), isAuthenticated: true, isLoading: false })
    } catch {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      setState({ user: null, isAuthenticated: false, isLoading: false })
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  /** Stores the tokens and exposes the user, exactly as every sign-in path must. */
  const persistSession = ({ tokens, user }: SessionPayload) => {
    localStorage.setItem('accessToken', tokens.accessToken)
    localStorage.setItem('refreshToken', tokens.refreshToken)
    setState({ user: toUser(user), isAuthenticated: true, isLoading: false })
    return user
  }

  const login = async (email: string, password: string) => {
    // Backend LoginCommand expects `credential` (email, username, or
    // phone) — sending `email` fails the NotEmpty validator with 400.
    // Mirrors mobile AuthRepository.login(credential, password).
    const res = await api.post('/auth/login', { credential: email, password })
    // Backend returns: { user: UserDto, tokens: { accessToken, refreshToken, expiresAt } }
    return persistSession(res.data)
  }

  /**
   * Signs in with the device key enrolled in `/biometric-setup`: local user verification (when the
   * device has a WebAuthn credential), then challenge → ECDSA signature → authenticate.
   */
  const loginWithBiometrics = async () => {
    const device = await loadDevice()
    if (!device) throw new BiometricNotConfiguredError()

    if (device.webauthnCredentialId && !(await verifyUser(device.webauthnCredentialId))) {
      throw new BiometricCancelledError()
    }

    const { challengeId, challenge } = await requestBiometricChallenge(device.deviceId)
    const signedChallenge = await signChallenge(device.privateKey, challenge)
    const auth = await authenticateBiometric({ deviceId: device.deviceId, challengeId, signedChallenge })
    return persistSession(auth)
  }

  const register = async (data: {
    email: string
    /** Set for phone sign-ups (after the SMS code was verified), as in the mobile app. */
    phone?: string
    username: string
    password: string
    accountType?: 'client' | 'creator' | 'agency'
    /** Optional gender (`POST /api/auth/register` accepts male/female/unspecified; omitted = unset). */
    gender?: GenderCanonical
  }) => {
    // Back-compat: callers that don't pass accountType default to
    // 'client'. The mobile app's spec required this to fix a bug
    // where every Creator/Agency sign-up was silently saved as
    // 'client'. The PWA had the same bug.
    const payload = {
      email: data.email,
      username: data.username,
      password: data.password,
      accountType: data.accountType ?? 'client',
      ...(data.phone !== undefined && { phone: data.phone }),
      ...(data.gender !== undefined && { gender: data.gender }),
    }
    const res = await api.post('/auth/register', payload)
    return persistSession(res.data)
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      // Drop every cached query (feed, notifications, KYC status…)
      // so the next account never renders the previous user's data.
      queryClient.clear()
      setState({ user: null, isAuthenticated: false, isLoading: false })
    }
  }

  const logoutAll = async () => {
    try {
      await api.post('/auth/logout-all')
    } finally {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      // Same cross-account hygiene as logout (see above).
      queryClient.clear()
      setState({ user: null, isAuthenticated: false, isLoading: false })
    }
  }

  return {
    ...state,
    login,
    loginWithBiometrics,
    persistSession,
    register,
    logout,
    logoutAll,
    refresh: checkAuth
  }
}
